import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { formatDateTime } from '../../utils/dateUtils';
import { ResolveForm, PartnerKontoForm, GkDecisions, actionsFor } from './GkResolve';
import type { GkFinding, GkStatusMeta, PaymentGkException, PaymentGkStatus } from './types';

/** The last calculation failed and no later one succeeded: the job tries again only in a few minutes. */
export function gkLastRunFailed(meta: GkStatusMeta | null | undefined): boolean {
  if (!meta || !meta.error || !meta.errorAt) return false;
  return !meta.computedAt || new Date(meta.errorAt) > new Date(meta.computedAt);
}

/**
 * The Vasco GK box of the payment page: the stored GK status of the payment
 * (server/utils/gkPaymentStatus.js), its exceptions with "Resolve" as in the
 * GK preview ("Add konto" for a B2), and its decisions with "Remove". The
 * order rows of the page get a red mark with the code of the exception that
 * names them.
 */

export const GK_BOX_ID = 'gk-box';
const POLL_MS = 10000;
// After a failed calculation the job waits 5 minutes before it tries again.
const POLL_AFTER_FAILURE_MS = 60000;

export interface PaymentGkData {
  gk: PaymentGkStatus;
  meta: GkStatusMeta;
  /** Decisions can be saved and removed (GK_EXPORT_ENABLED on the server). */
  enabled: boolean;
  /** The payment this answer is for (set by usePaymentGk). */
  forId?: string;
}

/**
 * Loads the GK status of a payment, again when `refreshKey` changes, and
 * every POLL_MS while a recalculation is pending.
 */
export function usePaymentGk(paymentId: string | number | undefined, refreshKey: number) {
  const [data, setData] = useState<PaymentGkData | null>(null);
  const [error, setError] = useState('');
  // Only the newest request writes, so a slow older answer never wins.
  const seq = useRef(0);

  const load = useCallback(async () => {
    if (paymentId === undefined) return;
    const mine = ++seq.current;
    try {
      const res = await axios.get<PaymentGkData>(`/api/accounting/gk-export/payments/${paymentId}/status`);
      if (mine !== seq.current) return;
      // The box must never break the payment page: an answer without the
      // expected shape shows no box.
      const ok = res.data && res.data.gk && res.data.meta;
      setData(ok ? { ...res.data, forId: String(paymentId) } : null);
      setError('');
    } catch (err: any) {
      if (mine !== seq.current) return;
      setError(err?.response?.data?.error || 'Could not load the GK status.');
    }
  }, [paymentId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // On another payment's page (the same page component with a new id) the
  // answer for the previous payment is never shown, also while loading or
  // when the request fails.
  const current = data && data.forId === String(paymentId) ? data : null;

  const pending = current?.meta.pending === true;
  const failed = gkLastRunFailed(current?.meta);
  useEffect(() => {
    if (!pending) return undefined;
    const timer = setInterval(load, failed ? POLL_AFTER_FAILURE_MS : POLL_MS);
    return () => clearInterval(timer);
  }, [pending, failed, load]);

  // After a change on this page (a saved or removed decision) the server has
  // already marked the data as changed. Show "updating" at once instead of
  // after the reload, and load again; the answer replaces this guess.
  // Not when the job is switched off: then nothing will recalculate.
  const changed = useCallback(() => {
    setData((d) => (d && d.meta.jobEnabled !== false ? { ...d, meta: { ...d.meta, pending: true } } : d));
    load();
  }, [load]);

  return { data: current, error, reload: load, changed };
}

/** The exceptions of the payment that name an order row (by order number or by a transaction of the row). */
export function gkExceptionsFor(gk: PaymentGkStatus | null | undefined, row: { orderNumber?: string | null; txnIds?: string[] }): PaymentGkException[] {
  if (!gk || (gk.state !== 'blocked' && gk.state !== 'ready')) return [];
  const txns = new Set(row.txnIds || []);
  const order = row.orderNumber ? String(row.orderNumber) : null;
  return gk.exceptions.filter((e) => {
    if (order && e.orderNumber && String(e.orderNumber) === order) return true;
    if (order && (e.orderNumbers || []).map(String).includes(order)) return true;
    const txn = e.itemKey && /^(rt|bt|pp):/.test(e.itemKey) ? e.itemKey.slice(3) : null;
    return txn !== null && txns.has(txn);
  });
}

/**
 * The partner of a B2 that the konto map can fix. Statuses calculated before
 * the field existed have the code only in the message text.
 */
export function b2PartnerSifra(e: PaymentGkException): number | null {
  if (e.code !== 'B2') return null;
  if (e.partnerSifra !== undefined && e.partnerSifra !== null) return e.partnerSifra;
  const m = /no partner-konto row for partner (\d+)\.$/.exec(e.message);
  return m ? Number(m[1]) : null;
}

const exceptionAnchor = (e: PaymentGkException) => `gk-exc-${e.code}-${e.itemKey || 'none'}`;

/** The red mark of an order row: the codes, a click scrolls to the exception in the GK box. */
export const GkRowMark: React.FC<{ exceptions: PaymentGkException[] }> = ({ exceptions }) => {
  if (exceptions.length === 0) return null;
  const codes = [...new Set(exceptions.map((e) => e.code))].join(', ');
  const goTo = () => {
    const el = document.getElementById(exceptionAnchor(exceptions[0])) || document.getElementById(GK_BOX_ID);
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  return (
    <button
      type="button"
      onClick={goTo}
      title={`Holds the GK export: ${exceptions.map((e) => `${e.code}: ${e.message}`).join(' ')}`}
      className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-800 hover:bg-red-200 flex-shrink-0"
      data-testid="gk-row-mark"
    >
      GK {codes}
    </button>
  );
};

/**
 * One exception with its help text and "Resolve" (when the preview offers a
 * decision for it). The box keeps one form open at a time, as the preview.
 */
const ExceptionRow: React.FC<{
  e: PaymentGkException;
  paymentId: number;
  day: string;
  enabled: boolean;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSaved: () => void;
}> = ({ e, paymentId, day, enabled, open, onOpen, onClose, onSaved }) => {
  const finding: GkFinding = {
    code: e.code, level: 'block', paymentId, day, itemKey: e.itemKey, orderNumber: e.orderNumber, message: e.message, help: e.help,
  };
  const canResolve = actionsFor(finding).length > 0;
  const kontoPartner = b2PartnerSifra(e);
  const offTitle = enabled ? undefined : 'Decisions are switched off on this server (GK_EXPORT_ENABLED).';
  return (
    <li id={exceptionAnchor(e)} className="py-2">
      <div className="flex flex-wrap items-start gap-x-3 gap-y-1 text-sm">
        <span className="font-semibold text-red-700">{e.code}</span>
        {e.orderNumber && <span className="text-gray-700">Order #{e.orderNumber}</span>}
        <span className="min-w-0 flex-1 text-gray-800">{e.message}</span>
        {canResolve && (
          <button
            type="button"
            onClick={onOpen}
            disabled={!enabled || open}
            title={offTitle}
            className="rounded border border-blue-300 px-2 py-0.5 text-xs text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Resolve
          </button>
        )}
        {kontoPartner !== null && (
          <button
            type="button"
            onClick={onOpen}
            disabled={!enabled || open}
            title={offTitle}
            className="rounded border border-blue-300 px-2 py-0.5 text-xs text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Add konto
          </button>
        )}
      </div>
      {e.help && <p className="mt-0.5 text-xs text-gray-500">{e.help}</p>}
      {open && kontoPartner !== null && (
        <PartnerKontoForm
          partnerSifra={kontoPartner}
          onClose={onClose}
          onSaved={() => {
            onClose();
            onSaved();
          }}
        />
      )}
      {open && canResolve && (
        <ResolveForm
          finding={finding}
          onClose={onClose}
          onSaved={() => {
            onClose();
            onSaved();
          }}
        />
      )}
    </li>
  );
};

export const PaymentGkBox: React.FC<{
  paymentId: number;
  data: PaymentGkData | null;
  error: string;
  onChanged: () => void;
}> = ({ paymentId, data, error, onChanged }) => {
  const [decisionsKey, setDecisionsKey] = useState(0);
  // The exception whose Resolve form is open (code|itemKey); one at a time.
  const [openKey, setOpenKey] = useState<string | null>(null);
  if (!data && !error) return null;
  if (data && data.gk.state === 'none') return null;

  const changed = () => {
    setDecisionsKey((k) => k + 1);
    onChanged();
  };
  const gk = data?.gk;
  const pending = data?.meta.pending === true;
  const failed = gkLastRunFailed(data?.meta);
  const jobOff = data?.meta.jobEnabled === false;

  let summary: React.ReactNode = null;
  if (gk?.state === 'exported') summary = <span className="text-gray-700">Exported to GK in document {gk.dokument}.</span>;
  else if (gk?.state === 'unknown') {
    summary = (
      <span className="text-gray-600">
        {gk.reason === 'export_cancelled' ? 'The GK export of this payment was cancelled. The status is being calculated again.' : 'Not calculated yet.'}
      </span>
    );
  } else if (gk?.state === 'ready') {
    summary = (
      <span>
        <span className="font-medium text-green-700">Ready.</span>{' '}
        {gk.dayHeld
          ? `No problem with this payment. Day ${gk.day} is held (${gk.dayCodes.join(', ')})${gk.heldBy.length > 0 ? ` by payment ${gk.heldBy.join(', ')}` : ''}, so it is not exported yet.`
          : 'This payment can be exported.'}
      </span>
    );
  } else if (gk?.state === 'blocked') {
    summary = <span className="font-medium text-red-700">This payment holds the GK export. Solve each problem below.</span>;
  }

  return (
    <div id={GK_BOX_ID} className="bg-white border border-gray-200 rounded-lg shadow-sm" data-testid="gk-box">
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex flex-wrap items-baseline gap-x-3">
        <h3 className="text-lg font-medium text-gray-900">Vasco GK</h3>
        {pending && (
          <span className="text-sm text-gray-500" title="Data changed after the last calculation.">
            (updating…)
          </span>
        )}
      </div>
      <div className="px-6 py-4 space-y-3 text-sm">
        {error && <p className="text-red-700">{error}</p>}
        {pending && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-amber-800" data-testid="gk-box-updating">
            The data of the GK export changed. The status below is from the last calculation
            {data?.meta.computedAt ? ` (${formatDateTime(data.meta.computedAt)})` : ''} and can be out of date.
            {failed ? ' The last calculation failed; the next try is in a few minutes.' : ' It updates by itself in about half a minute.'}
          </p>
        )}
        {jobOff && (
          <p className="rounded-md bg-gray-100 px-3 py-2 text-gray-700" data-testid="gk-box-job-off">
            The GK status is not calculated again on this server (GK_STATUS_JOB_DISABLED). The status below
            {data?.meta.computedAt ? ` is from ${formatDateTime(data.meta.computedAt)} and` : ''} can be out of date.
            The GK preview shows the current state.
          </p>
        )}
        {data?.meta.error && (
          <p className="text-red-700">The last GK calculation failed: {data.meta.error}</p>
        )}
        {summary && <p>{summary}</p>}
        {gk?.state === 'blocked' && (
          <ul className="divide-y divide-gray-100">
            {gk.exceptions.map((e, i) => {
              // Not the message: it can change on a recalculation, and a new key would close an open form.
              const key = `${e.code}|${e.itemKey}`;
              const n = gk.exceptions.slice(0, i).filter((x) => `${x.code}|${x.itemKey}` === key).length;
              const rowKey = n === 0 ? key : `${key}|${n}`;
              return (
                <ExceptionRow
                  key={rowKey}
                  e={e}
                  paymentId={paymentId}
                  day={gk.day}
                  enabled={data?.enabled === true}
                  open={openKey === rowKey}
                  onOpen={() => setOpenKey(rowKey)}
                  onClose={() => setOpenKey((k) => (k === rowKey ? null : k))}
                  onSaved={changed}
                />
              );
            })}
          </ul>
        )}
        {data && !data.enabled && (
          <p className="text-xs text-gray-500">Decisions are switched off on this server (GK_EXPORT_ENABLED), so Resolve and Remove are not available.</p>
        )}
        {data && (
          <div>
            <h4 className="mb-1 text-xs font-medium uppercase text-gray-500">Decisions</h4>
            <GkDecisions paymentId={paymentId} refreshKey={decisionsKey} enabled={data.enabled} onChanged={changed} />
          </div>
        )}
      </div>
    </div>
  );
};
